// src/modules/competitions/pages/CompetitionDetail.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Loading from '../../../shared/components/Loading';
import ErrorMessage from '../../../shared/components/ErrorMessage';
import CompetitionDetails from '../components/CompetitionDetails';
import TermsAcceptance from '../components/TermsAcceptance';
import {
  CompetitionDetails as CompetitionDetailsType,
  CompetitionRegistration,
  RegistrationData,
  CategoryRegistrationData
} from '../types';
import { competitionService } from '../services/competitionService';
import { useAppContext } from '../../../shared/context/AppContext';
import Button from '../../../shared/components/ui/Button';
import '../styles/competitionDetails.css';
import JudgeApplicationButton from '../components/JudgeApplicationButton';
import JudgeTermsModal from '../components/JudgeTermsModal';
import { judgeService } from '../services/judgeService';
import { JudgeApprovalStatus } from '../types/judge';
import Card from '../../../shared/components/ui/Card';
import { getFullImageUrl } from '../../../shared/services/apiConfig';

type ViewMode = 'details' | 'terms' | 'registered';

const CompetitionDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { state } = useAppContext();
  const { auth } = state;

  const [competitionDetails, setCompetitionDetails] = useState<CompetitionDetailsType | null>(null);
  const [myRegistration, setMyRegistration] = useState<CompetitionRegistration | null>(null);
  const [currentView, setCurrentView] = useState<ViewMode>('details');
  const [isLoading, setIsLoading] = useState(true);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [termsId, setTermsId] = useState<number | null>(null);
  const [isParticipant, setIsParticipant] = useState(false);
  const [judgeStatus, setJudgeStatus] = useState<JudgeApprovalStatus | null>(null);
  const [showJudgeTermsModal, setShowJudgeTermsModal] = useState(false);
  const [checkingParticipant, setCheckingParticipant] = useState(true);

  const competitionId = parseInt(id || '0');

  useEffect(() => {
    if (competitionId) {
      loadCompetitionDetails();
    }
  }, [competitionId, auth.isAuthenticated]);

  useEffect(() => {
    if (competitionDetails && auth.isAuthenticated) {
      checkParticipantAndJudgeStatus();
    }
  }, [competitionDetails, auth.isAuthenticated]);

  /**
   * Verificar si el usuario es participante y su estado como juez
   */
  const checkParticipantAndJudgeStatus = async () => {
    if (!id) return;

    try {
      setCheckingParticipant(true);

      // 1. Verificar si es participante
      const participantResponse = await competitionService.checkParticipant(Number(id));

      if (participantResponse.success && participantResponse.data) {
        setIsParticipant(participantResponse.data.is_participant);
      }

      // Si es participante, no puede ser juez
      if (participantResponse.data?.is_participant) {
        setJudgeStatus(null);
        return;
      }

      // 2. Verificar si tiene registro como juez en competition_registrations
      const registrationResponse = await judgeService.checkRegistration(Number(id));

      if (registrationResponse.success && registrationResponse.data?.has_registration) {
        const regStatus = registrationResponse.data.approval_status;

        // ✅ CORRECCIÓN: Establecer estado según aprobación
        if (regStatus === 'pending') {
          setJudgeStatus('pending');
        } else if (regStatus === 'rejected') {
          setJudgeStatus('rejected');
        } else if (regStatus === 'approved') {
          // ✅ APROBADO: Puede ir al panel de juez
          setJudgeStatus('approved');
        }
        return;
      }

      // 3. Si no tiene registro, puede postular
      setJudgeStatus(null);

    } catch (err) {
      console.error('Error checking participant/judge status:', err);
    } finally {
      setCheckingParticipant(false);
    }
  };

  /**
   * Manejar clic en "Postular como Juez"
   */
  const handleApplyAsJudge = () => {
    setShowJudgeTermsModal(true);
  };

  /**
   * Manejar aceptación de términos de juez
   */
  const handleAcceptJudgeTerms = async (acceptedTermsId: number) => {
    try {
      setIsActionLoading(true);
      setActionError(null);

      // Llamar al nuevo endpoint
      const response = await judgeService.acceptTerms({
        event_id: Number(id),
        terms_id: acceptedTermsId
      });

      if (!response.success) {
        setActionError(response.message || 'Error al aceptar términos');
        return;
      }

      // Cerrar modal
      setShowJudgeTermsModal(false);

      // Actualizar estado a pendiente
      setJudgeStatus('pending');

    } catch (err) {
      setActionError('Error al enviar solicitud');
    } finally {
      setIsActionLoading(false);
    }
  };

  /**
   * Ir al panel de juez
   */
  const handleNavigateToJudgePanel = () => {
    navigate(`/judge/evaluations/${id}`);
  };

  // ✅ DESPUÉS
const loadCompetitionDetails = async () => {
  try {
    setIsLoading(true);
    setError(null);

    const response = await competitionService.getCompetitionDetails(competitionId);

    if (response.success && response.data) {
      // ✅ Ya viene en el formato correcto
      setCompetitionDetails(response.data);
      console.log(response.data)
      if (response.data.my_registration) {
        setMyRegistration(response.data.my_registration);
        setCurrentView('registered');
      }
    } else {
      setError(response.message || 'Error al cargar detalles de la competencia');
    }
  } catch (err) {
    setError('Error al cargar detalles de la competencia');
  } finally {
    setIsLoading(false);
  }
};

  const handleStartRegistration = async () => {
    try {
      setIsActionLoading(true);
      setActionError(null);

      const termsResponse = await competitionService.getTermsAndConditions(competitionId, 'participant');

      if (termsResponse.success && termsResponse.data) {
        setTermsId(termsResponse.data.id);
        setCurrentView('terms');
      } else {
        setActionError('No se pudieron cargar los términos y condiciones');
      }
    } catch (err) {
      setActionError('Error al cargar términos y condiciones');
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleAcceptTerms = async () => {
    if (!termsId) return;

    try {
      setIsActionLoading(true);
      setActionError(null);

      const registrationData: RegistrationData = {
        event_id: competitionId,
        terms_id: termsId
      };

      const response = await competitionService.registerInCompetition(registrationData);

      if (response.success && response.data) {
        await loadCompetitionDetails();
        setCurrentView('registered');
      } else {
        setActionError(response.message || 'Error al registrarse');
      }
    } catch (err) {
      setActionError('Error al registrarse en la competencia');
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleRegisterInCategory = async (categoryId: number, level?: string | null) => {
    if (myRegistration?.approval_status !== 'approved') {
      setActionError('Debes estar aprobado en la competencia para inscribirte en categorías');
      return;
    }

    try {
      setIsActionLoading(true);
      setActionError(null);

      const categoryData: CategoryRegistrationData = {
        event_id: competitionId,
        category_id: categoryId,
        ...(level && { level })
      };

      const response = await competitionService.registerInCategory(categoryData);

      if (response.success) {
        await loadCompetitionDetails();
      } else {
        setActionError(response.message || 'Error al inscribirse en categoría');
      }
    } catch (err) {
      setActionError('Error al inscribirse en categoría');
    } finally {
      setIsActionLoading(false);
    }
  };

  const renderCurrentView = () => {
    if (!competitionDetails) return null;

    switch (currentView) {
      case 'terms':
        return (
          <TermsAcceptance
            competitionId={competitionId}
            onAccept={handleAcceptTerms}
            onBack={() => setCurrentView('details')}
            isLoading={isActionLoading}
            error={actionError}
          />
        );

      case 'registered':
        return (
          <div className="registered-view">
            {myRegistration && (
              <div className="registration-status-card">
                <h3>Estado de Registro</h3>
                <div className="status-info">
                  <span className="status-label">Estado:</span>
                  <span
                    className={`status-badge status-${myRegistration.approval_status}`}
                    style={{
                      backgroundColor: competitionService.getApprovalStatusColor(myRegistration.approval_status)
                    }}
                  >
                    {competitionService.getApprovalStatusText(myRegistration.approval_status)}
                  </span>
                </div>

                {myRegistration.approval_status === 'pending' && (
                  <p className="status-message">
                    Tu registro está pendiente de aprobación por los administradores.
                  </p>
                )}

                {myRegistration.approval_status === 'rejected' && myRegistration.rejection_reason && (
                  <div className="rejection-info">
                    <p className="rejection-reason">
                      <strong>Motivo del rechazo:</strong> {myRegistration.rejection_reason}
                    </p>
                  </div>
                )}
              </div>
            )}

            <CompetitionDetails
              details={competitionDetails}
              onRegisterInCategory={
                myRegistration?.approval_status === 'approved'
                  ? handleRegisterInCategory
                  : undefined
              }
              myCategories={competitionDetails.my_categories}
            />
          </div>
        );

      case 'details':
      default:
        return (
          <CompetitionDetails
            details={competitionDetails}
            onRegister={handleStartRegistration}
            showRegisterButton={!myRegistration}
          />
        );
    }
  };

  if (isLoading) {
    return <Loading message="Cargando detalles de la competencia..." />;
  }

  if (error || !competitionDetails) {
    return (
      <div className="competition-detail-error">
        <ErrorMessage
          message={error || 'Competencia no encontrada'}
          type="error"
        />
        <Button
          className="btn btn-primary"
          onClick={() => navigate('/competitions')}
        >
          Volver a competencias
        </Button>
      </div>
    );
  }

  return (
    <div className="competition-detail-page">
      <div className="detail-header">
        <Button
          className="back-btn"
          onClick={() => navigate('/competitions')}
        >
          ← Volver a competencias
        </Button>

        <div className="header-info">
          <h1>{competitionDetails.competition.title}</h1>
          {competitionDetails.competition.category_name && (
            <span
              className="category-badge"
              style={{ backgroundColor: competitionDetails.competition.category_color }}
            >
              {competitionDetails.competition.category_name}
            </span>
          )}
        </div>
      </div>

      {actionError && (
        <ErrorMessage
          message={actionError}
          type="error"
          onClose={() => setActionError(null)}
        />
      )}

      {/* Sección de Jueces - Solo para usuarios autenticados */}
      {auth.isAuthenticated && competitionDetails && (
        <Card variant="glass" className="judge-section">
          <h3>👨‍⚖️ Postular como Juez</h3>

          {checkingParticipant ? (
            <Loading message="Verificando estado..." />
          ) : (
            <JudgeApplicationButton
              isParticipant={isParticipant}
              judgeStatus={judgeStatus}
              onApply={handleApplyAsJudge}
              onNavigateToPanel={handleNavigateToJudgePanel}
            />
          )}
        </Card>
      )}

      {competitionDetails && (
        <JudgeTermsModal
          isOpen={showJudgeTermsModal}
          onClose={() => setShowJudgeTermsModal(false)}
          eventId={Number(id)}
          eventTitle={competitionDetails.competition.title}
          onAccept={handleAcceptJudgeTerms}
        />
      )}

      <div className="detail-content">
        {renderCurrentView()}
      </div>
    </div>
  );
};

export default CompetitionDetail;